import { useInfiniteFeed, useSourceCounts, useSyncStatus } from '@/hooks/useApi';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ActivityFeed from '@/components/ActivityFeed';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Loader2, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// LinkedIn is omitted: there's no viable way to read a personal feed (official API needs
// Partner Program approval; unofficial scrapers are being actively shut down by LinkedIn
// litigation). Revisit if a workable option surfaces.
const sourceFilters = [
  { value: '', label: 'All' },
  { value: 'github', label: 'GitHub' },
  { value: 'twitter', label: 'X' },
  { value: 'blog', label: 'Blog' },
  { value: 'search', label: 'Mentions' },
];

export default function HomePage() {
  useDocumentMeta();
  const [selectedSource, setSelectedSource] = useState('');
  const { 
    data, 
    isLoading, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useInfiniteFeed({
    source: selectedSource || undefined,
    limit: 20,
  });
  
  const { data: counts } = useSourceCounts();
  const { data: syncStatus } = useSyncStatus();
  const { ref, inView } = useInView();

  // Hide a source's filter pill (never "All") when it has no data and has
  // been synced at least once — avoids showing a permanently-empty,
  // broken-looking tab for a source with bad credentials. Deliberately not
  // limited to status === 'failed': a sync service can catch its own errors
  // and report "completed" with 0 items rather than "failed" (this is what
  // LinkedIn's did before it was removed from this list entirely), so a
  // source can be silently non-functional without ever failing. "never"
  // status (not yet attempted) still shows, to give a fresh source the
  // benefit of the doubt on first load.
  const visibleFilters = sourceFilters.filter((filter) => {
    if (!filter.value) return true;
    const status = syncStatus?.[filter.value]?.status;
    const count = counts?.[filter.value] ?? 0;
    return !(count === 0 && status && status !== 'never');
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  // Flatten pages into a single array
  const feedItems = data?.pages.flat() || [];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="sr-only">Javier Callico — Software Engineer</h1>
      {/* Activity Feed Section */}
      <section>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Recent Activity
          </h2>

          {/* Source Filters */}
          <div className="flex flex-wrap gap-2">
            {visibleFilters.map((filter) => {
              const count = filter.value ? counts?.[filter.value] || 0 : undefined;
              const sourceStatus = filter.value ? syncStatus?.[filter.value] : undefined;
              const isStale = sourceStatus?.status === 'failed';
              const staleTooltip = isStale
                ? sourceStatus?.lastSuccessfulSync
                  ? `${filter.label} sync is currently unavailable — showing data from ${formatDistanceToNow(new Date(sourceStatus.lastSuccessfulSync), { addSuffix: true })}`
                  : `${filter.label} sync is currently unavailable`
                : undefined;

              return (
                <button
                  key={filter.value}
                  onClick={() => setSelectedSource(filter.value)}
                  title={staleTooltip}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedSource === filter.value
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {filter.label}
                  {count !== undefined && count > 0 && (
                    <span className="ml-1 text-xs opacity-70">({count})</span>
                  )}
                  {isStale && (
                    <AlertTriangle className="w-3 h-3 text-amber-500 dark:text-amber-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <ActivityFeed items={feedItems} isLoading={isLoading} />
        
        {/* Infinite Scroll Trigger */}
        {(hasNextPage || isFetchingNextPage) && (
          <div ref={ref} className="py-8 flex justify-center">
            {isFetchingNextPage ? (
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            ) : (
              <div className="h-4" />
            )}
          </div>
        )}
      </section>
    </div>
  );
}
